import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { OAuthService, AuthConfig, OAuthErrorEvent } from 'angular-oauth2-oidc';
import { Observable, from, of, Subject, BehaviorSubject, throwError } from 'rxjs';
import { ConfigService } from '../config/config.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { take, map, switchMap, filter, catchError } from 'rxjs/operators';
import { Authorization } from '../../models/authorization.model';
import { TokenService } from './token.service';
import { RoleSelectionService } from '../role-selection/role-selection.service';
import { UserLoginAction, UserLogoutAction } from '../../store/actions/auth/auth.actions';
import { Store, select } from '@ngrx/store';
import { AppState } from '../../store';
import { UserProfile } from '../../models/userprofile.model';
import { getUser } from '../../store/selectors/auth/auth.selectors';
import { ModalDismissReasons, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SelectRoleCallbackComponent } from '../../components/select-role-callback/select-role-callback.component';
import { UnauthorizedComponent } from '../../components/unauthorized/unauthorized.component';


@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public static readonly preAuthTypeKeyFull = 'full';
  public authorization: Authorization;
  public authorizationSubject = new Subject<Authorization>();
  private enableLogging = false;
  private user$: Observable<UserProfile> = this.store.pipe(select(getUser));
  private roles: { name: string; value: string }[];
  public rolesSubject = new BehaviorSubject<{ name: string; value: string }[]>([]);
  private interceptorUrls;
  private isAuthenticating = false;

  constructor(
    private configService: ConfigService, 
    private http: HttpClient, 
    private roleSelectionService: RoleSelectionService, 
    private tokenService: TokenService,
    private oauthService: OAuthService,
    private store: Store<AppState>,
    private router: Router,
    private modalService: NgbModal) {

    this.enableLogging = this.configService.getSettings('authcfg_enable_logging');

    const config: AuthConfig = {
      issuer: this.configService.getSettings('authcfg_issuer'),
      clientId: this.configService.getSettings('authcfg_client_id'),
      responseType: this.configService.getSettings('authcfg_response_type'),
      redirectUri: `${window.location.origin}${this.configService.getSettings('authcfg_redirect_uri')}`,
      silentRefreshRedirectUri: `${window.location.origin}${this.configService.getSettings('authcfg_silent_redirect_uri')}`,
      scope: this.configService.getSettings('authcfg_scope'),
      useSilentRefresh: this.configService.getSettings('authcfg_automaticSilentRenew'),
      silentRefreshTimeout: this.configService.getSettings('authcfg_silentRefreshTimeout'),
      timeoutFactor: this.configService.getSettings('authcfg_timeoutFactor'),
      sessionChecksEnabled: this.configService.getSettings('authcfg_sessionChecksEnabled'),
      showDebugInformation: this.configService.getSettings('authcfg_showDebugInformation'),
      nonceStateSeparator: this.configService.getSettings('authcfg_nonceStateSeparator'), // Real semicolon gets mangled by IdentityServer's URI encoding,
      userinfoEndpoint: this.configService.getSettings('authcfg_userinfoEndpoint'),
      tokenEndpoint: this.configService.getSettings('authcfg_token_endpoint'),
      loginUrl: this.configService.getSettings('authcfg_authorization_endpoint'),
      logoutUrl: this.configService.getSettings('authcfg_end_session_endpoint'),
    };

    this.oauthService.configure(config);

    this.oauthService.events
      .pipe(filter(e => ['token_received'].includes(e.type)))
      .subscribe(e => {
        const accessToken = this.oauthService.getAccessToken();
        sessionStorage.setItem('access_token', accessToken);        
        this.tokenService.setToken(accessToken);
        // Clear cached authorization so it's re-fetched with the new token
        this.authorization = null;
        const user: UserProfile = this.parseJwt(accessToken);
        this.store.dispatch(new UserLoginAction({user}));
        this.writeAuthDebug('User object is assgined.');
      });   

    this.oauthService.setupAutomaticSilentRefresh();
    this.oauthService.tryLogin();

    // Proactively refresh the token when network connectivity is restored.
    // After a disconnect, the silent refresh iframe may have failed, leaving
    // a stale token that the BFF will reject with 400.
    window.addEventListener('online', () => {
      console.log('Network reconnected — proactively refreshing token');
      // Clear cached authorization so it's re-fetched with the fresh token
      this.authorization = null;
      // Small delay to allow DNS/network stack to stabilize
      setTimeout(() => {
        if (this.oauthService.hasValidAccessToken()) {
          this.oauthService.silentRefresh().then(() => {
            const accessToken = this.oauthService.getAccessToken();
            if (accessToken) {
              sessionStorage.setItem('access_token', accessToken);
              this.tokenService.setToken(accessToken);
              const user: UserProfile = this.parseJwt(accessToken);
              this.store.dispatch(new UserLoginAction({ user }));
            }
            console.log('Token refreshed after network reconnect');
          }).catch(err => {
            console.warn('Silent refresh failed after reconnect, token may still be valid:', err);
          });
        }
      }, 2000);
    });

    this.oauthService.events.subscribe(event => {
      if (event instanceof OAuthErrorEvent) {
        console.error('OAuthErrorEvent Object:', event);
      } else {
        console.warn('OAuthEvent Object:', event);
      }
    });

    try{
      const keys = this.configService.getSettings('token-interceptor-keys');

      let interCeptorUrls = keys.map(key => {
        return this.configService.getSettings(key);
      });
      this.interceptorUrls = interCeptorUrls || [];
      this.tokenService.setInterceptorUrls(this.interceptorUrls);
      const accessToken = this.oauthService.getAccessToken();
      if (accessToken) {
        sessionStorage.setItem('access_token', accessToken);        
        this.tokenService.setToken(accessToken);
        const user: UserProfile = this.parseJwt(accessToken);
        this.store.dispatch(new UserLoginAction({user}));
        this.writeAuthDebug('User object is assigned.');
      }
    }catch(e){
      console.log(`Error in AuthService, constructor: ${e}`)
    }
  }

  public isLoggedIn(): Promise<boolean> {
    return this.oauthService.loadDiscoveryDocumentAndTryLogin().then(() => {
      return this.oauthService.hasValidAccessToken();
    }).catch(() => {
      return this.oauthService.hasValidAccessToken();
    });
  }



  public startAuthentication(): void {
    if (this.isAuthenticating) {
      console.log('Authentication already in progress, skipping...');
      return;
    }
    
    this.isAuthenticating = true;
    console.log('startAuthentication invoked...');
    
    // Reset authentication flag after 30 seconds as failsafe
    setTimeout(() => {
      this.isAuthenticating = false;
    }, 30000);
    
    // initial redirect to IdP
    // this.oauthService.initCodeFlow();

    this.oauthService.loadDiscoveryDocumentAndTryLogin().then(() => {
        try {
          // console.log("Try Login")
          if (!this.isValidToken) {
            this.login()
          } else {
            //
            this.oauthService
              .silentRefresh()
              .then(() => {
                const accessToken = this.oauthService.getAccessToken();
                if (accessToken) {
                  sessionStorage.setItem('access_token', accessToken);
                  this.tokenService.setToken(accessToken);
                }
                return Promise.resolve();
              })
              .catch((result) => {
                const errorResponsesRequiringUserInteraction = [
                  'interaction_required',
                  'login_required',
                  'account_selection_required',
                  'consent_required',
                ];

                if (
                  result &&
                  result.reason &&
                  errorResponsesRequiringUserInteraction.indexOf(result.reason.error) >= 0
                ) {
                  console.warn(
                    'User interaction is needed to log in, we will wait for the user to manually log in.'
                  );
                  return Promise.resolve();
                }
                return Promise.reject(result);
            })
            .then(() => {    
              // Check for the strings 'undefined' and 'null' just to be sure. Our current
              // login(...) should never have this, but in case someone ever calls
              // initImplicitFlow(undefined | null) this could happen.

              if (
                this.oauthService.state !== 'undefined' &&
                this.oauthService.state !== 'null'
              ) {
                let stateUrl = this.oauthService.state;
                if (stateUrl.startsWith('/') === false) {
                  stateUrl = decodeURIComponent(stateUrl);
                }
                console.log(
                  `There was state of ${this.oauthService.state}, so we are sending you to: ${stateUrl}`
                );
                
                this.router.navigateByUrl(stateUrl);
              }
            })
            .catch(() => 
              console.log('login complete')
              // this.isDoneLoadingSubject$.next(true)
          );
          }
        } catch (err) {
          console.log('Error during login: ' + JSON.stringify(err));
        }
      });
  }
  
  public login(targetUrl?: string): void {
    // Note: before version 9.1.0 of the library you needed to
    // call encodeURIComponent on the argument to the method.
    this.oauthService.initLoginFlow(targetUrl || this.router.url);
  }

  get isValidToken() {
    return this.oauthService.hasValidAccessToken();
  }

  public writeAuthDebug(objToLog: any): void {
    if (this.enableLogging) {
      console.log(JSON.stringify(objToLog));
    }
  }

  public hasPermission(application: string, accessLevel: string): boolean {
    if (!this.authorization) return false;
    const appPermissions = JSON.parse(this.authorization.sub);
    return appPermissions[application] && appPermissions[application][accessLevel];
  }

  private authorizationRouteMap = {
    'flight-info': 'flightList',
    'flight-list': 'flightList',
    capacity: 'gateCapacity',
    deice: 'deice',
    throughput: 'stationThroughput',
    'advanced-search': 'advancedSearch',
    'policy-engine': 'policyEngine',
    cancellations: 'cancellation',
    'irop-replay': 'iropReplay',
    'jv-gates': 'jvGates',
    'severe-weather-index': 'severeWeatherIndex',
    'air-traffic-control': 'airTrafficControl',
    diversionManagement: 'diversionManagement',
    'operational-codes': 'operationalCodes',
    'user-access': 'userAccess',
    'wit': 'wit'
  };

  setAuth(result, route) {
    const authMap = JSON.parse(result.sub);
    const path = route && route.url && route.url[0] && route.url[0].path ? route.url[0].path : this.router.url.split('/')[1];
    const authKey = this.authorizationRouteMap[path];
    if (authMap && authKey) {
      const access: boolean = authMap[authKey].access;
      console.log(`Authorized for ${authKey}:`, access);
      return access;
    }

    // If authKey is empty (home screen), allow access
    if (!authKey) {
      return true;
    }
    return false;
  }

  public completeAuthentication(router: any): void {
    this.writeAuthDebug('completeAuthentication invoked...');
    this.isAuthenticating = false; // Reset authentication flag

    // Determine whether this is a full redirect auth or silent redirect
    const authType = localStorage.getItem(this.configService.getSettings('preAuthTypeKey'));
    // this.writeAuthDebug(`authType is: ${authType}`);
    localStorage.removeItem(this.configService.getSettings('preAuthTypeKey'));

    if (authType != null && authType.toLowerCase() === AuthService.preAuthTypeKeyFull) {
      // Retrieve the original location requiring auth that the use was trying to access
      // and redirect her to it

      const nextUrl = localStorage.getItem(this.configService.getSettings('postAuthPageSessionKey'));
      if (nextUrl != null && nextUrl.length > 0) {
        localStorage.removeItem(this.configService.getSettings('postAuthPageSessionKey'));
        // this.writeAuthDebug('Re-Routing to next requested url: ' + nextUrl);
        router.navigateByUrl(nextUrl);
      } else {
        const originalUrl = localStorage.getItem(this.configService.getSettings('preAuthPageSessionKey'));
        localStorage.removeItem(this.configService.getSettings('preAuthPageSessionKey'));

        // this.writeAuthDebug('Re-Routing to originally requested url: ' + originalUrl);
        router.navigateByUrl(originalUrl);
      }
    }
  }


  public getUsername(): string {
    let username: string;
    this.user$.pipe(
      take(1),
      map(user=> `${user?.family_name}, ${user?.given_name}`),
    )
    .subscribe(res =>{
      username = res      
    });

    return username ? username : 'EMT User'
  }

  public getFullUsername() {
    let fullUsername: string;

    this.user$.pipe(
      take(1),
      map(user=> `${user?.preferred_username} ${user?.family_name}, ${user?.given_name}`),
    )
    .subscribe(res =>{
      fullUsername = res        
    });

    return fullUsername
  }

  public getUserId() {
    let userId: string;

    this.user$.pipe(
      take(1),
      map(user=> user.preferred_username),
    )
    .subscribe(res =>{
      userId = res      
    });

    return userId ? userId : null;
  }

  public getRoles() {
    this.rolesSubject.next(this.roles);
  }

  public startSignout() {
    console.log('logout invoked...');
    this.oauthService.logOut();
    this.store.dispatch(new UserLogoutAction());
  }

  public getAuthorization(selectedRole?: string, vipsRole?: string): Promise<Authorization> {
    const baseUrl = this.configService.getSettings('authorization');
    if (this.authorization) {
      this.writeAuthDebug('Using stored authorization');
      return Promise.resolve(this.authorization);
    }
    return this.store
      .pipe(
        select(getUser),
        take(1),
        switchMap(user => {
          if (user && this.oauthService.getAccessToken()) {
            this.writeAuthDebug('Making First BFF call to fetch authorization');
            const currentToken = this.oauthService.getAccessToken();
            if (!currentToken || !this.oauthService.hasValidAccessToken()) {
              return of(null);
            }
            const requestOptions = {
              headers: new HttpHeaders({ accessToken: currentToken }),
            };
            if (selectedRole) {
              requestOptions['params'] = { selectedRole };
            }
            if (vipsRole) {
              sessionStorage.setItem('rawVDIRole', vipsRole);
              requestOptions['params'] = { ...requestOptions['params'], 'Role': vipsRole };
            }

            return this.http.get<any>(baseUrl, requestOptions).pipe(
              map((results: { accessPermission?: string; roles: Array<string>; roleMap: { name: string; value: string }[]; selectedRole?: string; }) => {
                if (results && results.accessPermission) {
                  this.authorization = this.parseJwt(results.accessPermission);
                  this.tokenService.setToken(sessionStorage.getItem('access_token'));
                  this.tokenService.setInterceptorUrls(this.interceptorUrls);
                  this.authorizationSubject.next(this.authorization);

                  // Need to set role if response has selectedRole defined
                  if (results.selectedRole && results.selectedRole.length > 0) {
                    this.rolesSubject.next(results.roleMap);
                    this.roleSelectionService.setSelectedRole(results.selectedRole);
                  }
                  return this.authorization;
                } else {
                  this.roles = results.roleMap;
                  this.rolesSubject.next(this.roles);
                  return null;
                }
              }),
              catchError((error) => {
                // 400 from user-permission indicates a stale/invalid token or
                // server-side session loss (e.g. after network disconnect) —
                // force a token refresh and retry once before giving up.
                if ((error.status === 400 || error.status === 0) && !requestOptions['_retried']) {
                  return from(this.refreshTokenAndRetry(baseUrl, requestOptions, selectedRole, vipsRole));
                }
                return throwError(error);
              })
            );
          } else {
            return of(null);
          }
        })
      )
      .toPromise();
  }

  /**
   * Forces a silent token refresh and retries the user-permission call.
   * Addresses Edge browser behavior where the silent refresh iframe may fail
   * after network disconnect, leaving a stale token in sessionStorage.
   */
  private async refreshTokenAndRetry(
    baseUrl: string,
    originalOptions: any,
    selectedRole?: string,
    vipsRole?: string
  ): Promise<Authorization> {
    // Clear cached authorization so it's re-fetched with fresh token
    this.authorization = null;

    // Brief delay to allow network stack to stabilize after reconnect
    await new Promise(resolve => setTimeout(resolve, 1000));

    try {
      await this.oauthService.silentRefresh();
    } catch (refreshError) {
      console.warn('Silent refresh failed during retry, attempting full token reload', refreshError);
      // silentRefresh failed (Edge iframe issue) — try discovery reload
      try {
        await this.oauthService.loadDiscoveryDocumentAndTryLogin();
      } catch (discoveryError) {
        console.warn('Discovery reload also failed', discoveryError);
      }
    }

    const freshToken = this.oauthService.getAccessToken();
    if (!freshToken || !this.oauthService.hasValidAccessToken()) {
      // Token could not be refreshed — save current URL and force full redirect login
      const currentUrl = this.router.url;
      localStorage.setItem(this.configService.getSettings('preAuthPageSessionKey'), window.location.href.split(window.location.host)[1]);
      localStorage.setItem(this.configService.getSettings('postAuthPageSessionKey'), currentUrl);
      localStorage.setItem(this.configService.getSettings('preAuthTypeKey'), AuthService.preAuthTypeKeyFull);

      // Reset the authenticating guard so login() is not blocked
      this.isAuthenticating = false;
      this.login(currentUrl);

      // Return a rejected promise so the auth guard returns false and blocks navigation
      return Promise.reject('Token expired, redirecting to login');
    }

    // Update sessionStorage and TokenService with the fresh token
    sessionStorage.setItem('access_token', freshToken);
    this.tokenService.setToken(freshToken);

    // Rebuild request options with the fresh token
    const retryOptions: any = {
      headers: new HttpHeaders({ accessToken: freshToken }),
      _retried: true, // guard against infinite retry loop
    };
    if (selectedRole) {
      retryOptions['params'] = { selectedRole };
    }
    if (vipsRole) {
      retryOptions['params'] = { ...retryOptions['params'], 'Role': vipsRole };
    }

    const results: any = await this.http.get(baseUrl, retryOptions as any).toPromise();
    if (results && results.accessPermission) {
      this.authorization = this.parseJwt(results.accessPermission);
      this.tokenService.setToken(sessionStorage.getItem('access_token'));
      this.tokenService.setInterceptorUrls(this.interceptorUrls);
      this.authorizationSubject.next(this.authorization);

      if (results.selectedRole && results.selectedRole.length > 0) {
        this.rolesSubject.next(results.roleMap);
        this.roleSelectionService.setSelectedRole(results.selectedRole);
      }
      return this.authorization;
    } else {
      this.roles = results.roleMap;
      this.rolesSubject.next(this.roles);
      return null;
    }
  }

  // This is a method to parse a JWT token copied from here:
  // https://stackoverflow.com/a/38552302
  private parseJwt(token) {
    var base64Url = token.split('.')[1];
    var base64 = decodeURIComponent(
      atob(base64Url)
        .split('')
        .map(function(c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );

    return JSON.parse(base64);
  }

  /*
    @active-branch: what-if-submissions
  */
  public getPPR() {
    let ppr: string;

    this.user$.pipe(
      take(1),
      map(user=> user.sub),
    )
    .subscribe(res =>{
      ppr = res      
    });

    return ppr ? ppr : null;
  }

  /*
    @name getUserProperty
    @desc get property of the user (private) profile
    @active-branch: auth and bug
  */
  public getUserProperty(p: string) {
    let prop: string;

    this.user$.pipe(
      take(1),
      map(user=> user[p]),
    )
    .subscribe(res =>{
      prop = res      
    });

    return prop ? prop : null;
  }

  openRoleSelectionModal(route) {
    const modalRef = this.modalService.open(SelectRoleCallbackComponent, { centered: true, beforeDismiss: () => false });
    
    modalRef.result.then(
      selectedRole => {
        // Role is already set by the modal component via roleSelectionService
        console.log('Role selected:', selectedRole);
        return this.getAuthorization(selectedRole).then(secondResult => {
          if (secondResult && secondResult.sub) {
            const authorized = this.setAuth(secondResult, route);
            if (!authorized) {
              // this.modalService.open(UnauthorizedComponent, { centered: true });
              console.log('Not authorized to access the resource.');
              return false;
            }
          } else {
            this.openRoleSelectionModal(route);
          }
        });
      },
      reason => {
        console.log('dismissed with reason', this.getDismissReason(reason));
        // this.router.navigate(['/unauthorized']);
      }
    );
  }

   private getDismissReason(reason: any): string {
    if (reason === ModalDismissReasons.ESC) {
      return 'by pressing ESC';
    } else if (reason === ModalDismissReasons.BACKDROP_CLICK) {
      return 'by clicking on a backdrop';
    } else {
      return `with: ${reason}`;
    }
  }
}
